import React, { useCallback, useContext, useEffect, useState } from 'react';
import ActionLink, { ACTION_LINK_HEIGHT } from '../../../../Footer/ActionLink';
import { revenueShareFormToInput } from '../../SubmitGame/submission-helpers';
import { toast } from 'react-toastify';
import { useQuery } from '@apollo/client';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { submissionSubmitForApproval } from '../Mutations/submission-submit-for-approval';
import RevenueShareForm from '../RevenueShareForm/RevenueShareForm';
import { useHistory } from 'react-router-dom';
import { Box } from '@mui/material';
import { TIPALTI_USER_DATA_QUERY, TipaltiUserDataQuery } from '../../../Profile/profile-graphql';
import { StyledWarningText } from '../../../../../../common/Styleguide/Common/Text';
import { developerSubmissionUpdateLegalForm } from '../Mutations/submission-update-legal-form';
import BackToPreviewConfirmationPopup from '../BackToPreviewConfirmationPopup';

/**
 * Devs can land on this page:
 * 1. When they submit their submission for the first time, in basic launch.
 * 2. When they transition basic launch to full launch (or start directly in full launch).
 */
const LegalStep: React.FC = () => {
  const { revenueShareForm, submission, revenueShareFormValid, setAttemptedToSave } = useContext(SubmissionContext);
  const [isSaving, setIsSaving] = useState(false);
  const history = useHistory();
  const [showBackToPreviewDialog, setShowBackToPreviewDialog] = useState(false);

  const { data: tipaltiUserData, error: tipaltiError } = useQuery<TipaltiUserDataQuery>(TIPALTI_USER_DATA_QUERY);

  if (!submission) {
    throw new Error('Submission is not defined');
  }

  useEffect(() => {
    if (tipaltiError) {
      console.error('Error fetching payment details', tipaltiError);
      toast.error('Error fetching payment details, please ensure you have completed billing details in the Billing tab.');
    }
  }, [tipaltiError]);

  // new submissions that end of this page should also be submitted for approval after submitting the legal form
  const shouldAlsoSubmitForApproval = submission.status === 'DRAFT';

  const submit = useCallback(async () => {
    if (!revenueShareFormValid) {
      setAttemptedToSave(true);
      return;
    }

    try {
      setIsSaving(true);

      await developerSubmissionUpdateLegalForm({
        id: submission.id,
        revenueShareInputV3: revenueShareFormToInput(revenueShareForm),
      });

      if (shouldAlsoSubmitForApproval) {
        await submissionSubmitForApproval({
          id: submission.id,
        });
      }

      history.replace(`/games/${submission.id}`);
      window.location.reload();
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'UNKNOWN_ERROR';
      toast.error(errorMsg, { autoClose: 5000 });
      setIsSaving(false);
    }
  }, [revenueShareForm, revenueShareFormValid, setAttemptedToSave, submission, history, shouldAlsoSubmitForApproval]);

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      <RevenueShareForm />
      <BackToPreviewConfirmationPopup
        submission={submission}
        opened={showBackToPreviewDialog}
        onClose={() => {
          setShowBackToPreviewDialog(false);
        }}
      />
      {tipaltiUserData?.tipaltiUserData.hasCompletedPaymentDetails === false && (
        <StyledWarningText>
          Payment details not yet complete; fill them{' '}
          <a href={'/profile/billing'} target="_blank" rel="noreferrer" style={{ color: 'white', textDecoration: 'underline' }}>
            here
          </a>{' '}
          to be able to receive a payout once your game is fully launched!
        </StyledWarningText>
      )}
      <ActionLink
        primaryButton={{
          title: shouldAlsoSubmitForApproval ? 'Submit for approval' : 'Continue',
          callback: submit,
          isLoading: isSaving,
        }}
        middleButton={{
          title: 'Preview',
          callback: () => {
            setShowBackToPreviewDialog(true);
          },
        }}
        primaryButtonDisabled={!revenueShareFormValid}
        secondaryButton={{
          // leads you back to the file upload section
          title: 'Upload new version',
          callback: () => {
            history.replace({ search: '' }); // remove possible fromQA query param
            window.location.reload();
          },
        }}
      />
    </Box>
  );
};

export default LegalStep;
